/** Alignment report (STORY-059): planned versus taught versus assessed versus secure per
 * objective for any date range, a weekly timeline, an AI-drafted narrative the teacher edits,
 * CSV, print-to-PDF, and a push to the LMS where the institution has one connected. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, download } from "../api/client";
import { capabilities } from "../platform/capabilities";
import { useFlags } from "../platform/flags";
import { TeacherChrome } from "../teacher/Chrome";
import {
  alignmentCsvPath, generateNarrative, getAlignment, getCourse, getNarrative, isOfflineError, narrativeGeneration, pushAlignment, saveNarrative,
  type NarrativeBody, type NarrativeOut,
} from "../teacher/api";
import { Button, DraftChip, DraftFrame, SaveNotice, useFileSave } from "../ui";

const STATUS_GLYPH: Record<string, string> = { not_planned: "·", planned: "○", taught: "◐", assessed: "◑", secure: "■" };
const day = (d: Date) => d.toISOString().slice(0, 10);

export function Alignment() {
  const { courseId } = useParams({ strict: false }) as { courseId: string };
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const flags = useFlags();
  const [from, setFrom] = useState(() => day(new Date(Date.now() - 90 * 86400000)));
  const [to, setTo] = useState(() => day(new Date()));
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const report = useQuery({ queryKey: ["alignment", courseId, from, to], queryFn: () => getAlignment(courseId, from, to), retry: false, enabled: from <= to });
  const latest = useQuery({ queryKey: ["narrative", courseId, from, to], queryFn: () => getNarrative(courseId, from, to), retry: false, enabled: from <= to });
  const [editing, setEditing] = useState<NarrativeBody | null>(null);
  const [base, setBase] = useState<NarrativeOut | null>(null);
  const [gen, setGen] = useState<{ job: string | null; error: string | null }>({ job: null, error: null });
  const [pushed, setPushed] = useState<string | null>(null);

  useEffect(() => { setEditing(null); setBase(null); setPushed(null); }, [from, to]);
  useEffect(() => {
    if (editing === null && latest.data) { setBase(latest.data); setEditing(structuredClone(latest.data.narrative)); }
  }, [editing, latest.data]);
  useEffect(() => {
    if (!gen.job) return;
    const job = gen.job;
    const id = window.setInterval(async () => {
      try {
        const g = await narrativeGeneration(courseId, from, to, job);
        if (g.status === "done" || g.status === "failed") {
          window.clearInterval(id);
          setGen({ job: null, error: g.status === "failed" ? (g.error ?? "failed") : null });
          await qc.invalidateQueries({ queryKey: ["narrative", courseId, from, to] });
          if (g.narrative) { setBase(g.narrative); setEditing(structuredClone(g.narrative.narrative)); }
        }
      } catch (e) { window.clearInterval(id); setGen({ job: null, error: String(e) }); }
    }, 2500);
    return () => window.clearInterval(id);
  }, [gen.job, courseId, from, to, qc]);

  const generate = useMutation({
    mutationFn: () => generateNarrative(courseId, from, to),
    onSuccess: (g) => setGen({ job: g.job_id, error: null }),
    onError: (e) => setGen({ job: null, error: e instanceof ApiError && e.code === "ai_unavailable" ? t("align.aiUnavailable") : String(e) }),
  });
  const save = useMutation({
    mutationFn: (body: NarrativeBody) => saveNarrative(courseId, from, to, body),
    onSuccess: (n) => { setBase(n); setEditing(structuredClone(n.narrative)); void qc.invalidateQueries({ queryKey: ["narrative", courseId, from, to] }); },
  });
  const push = useMutation({ mutationFn: () => pushAlignment(courseId, from, to), onSuccess: (p) => setPushed(p.event_id) });
  const [csvError, setCsvError] = useState(false);
  const file = useFileSave();
  const downloadCsv = async () => {
    try {
      file.record(await download(`/analytics${alignmentCsvPath(courseId, from, to)}`, `alignment-${from}-${to}.csv`, "csv"));
      setCsvError(false);
    } catch {
      file.record(null);
      setCsvError(true);
    }
  };
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "short" }) : "–");
  const pct = (x: number | null) => (x === null ? "–" : `${Math.round(x * 100)}%`);
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  const rep = report.data;
  const notProjected = report.error instanceof ApiError && report.error.status === 404;
  const needsServer = isOfflineError(report.error);
  const dirty = !!editing && !!base && JSON.stringify(editing) !== JSON.stringify(base.narrative);
  const codeOf = (loId: string) => rep?.los.find((lo) => lo.lo_id === loId)?.code ?? loId;

  return (
    <TeacherChrome title={t("align.title")} course={course.data ? { id: courseId, title: course.data.title } : null} stage="dashboard" wide>
      {/* The chrome prints the title; the page adds the lede and the date range beside it. */}
      <header className="-mt-2 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <p className="measure text-sm text-[color:var(--ink-2)]">{t("align.lede")}</p>
        <form className="print:hidden flex flex-wrap items-end gap-2" onSubmit={(e) => e.preventDefault()}>
          <label className="flex flex-col gap-1 text-xs">{t("align.from")}<input type="date" className="field" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="flex flex-col gap-1 text-xs">{t("align.to")}<input type="date" className="field" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
          {rep && rep.los.length > 0 && (
            <>
              <Button variant="secondary" onClick={() => void downloadCsv()}>{t("align.csv")}</Button>
              {csvError && <p role="alert" className="text-sm" style={{ color: "var(--not-yet)" }}>{t("align.csvFailed")}</p>}
              <Button variant="secondary" onClick={() => void capabilities.printing.print()}>{t("align.pdf")}</Button>
              {flags.lti && (
                <Button variant="secondary" loading={push.isPending} disabled={!!pushed} onClick={() => push.mutate()}>{pushed ? t("align.pushed") : t("align.push")}</Button>
              )}
            </>
          )}
        </form>
      </header>
      {/* Where the CSV went. Silent in a browser, which keeps its own download list. */}
      <div className="print:hidden -mt-2"><SaveNotice result={file.result} /></div>

      {notProjected && <p className={card}>{t("align.notProjected")}</p>}
      {needsServer && <p className={card}>{t("dash.needsServer")}</p>}
      {rep && rep.los.length === 0 && <p className={card}>{t("align.empty")}</p>}
      {push.error && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{String(push.error)}</p>}

      {rep && rep.los.length > 0 && (
        <>
          <section className={card} aria-label={t("align.totals")}>
            <p className="text-sm text-[color:var(--ink-2)]">{t("align.period", { from: fmt(rep.range_from), to: fmt(rep.range_to), people: rep.totals.people })}</p>
            <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
              {(["planned", "taught", "assessed", "secure"] as const).map((k) => (
                <li key={k} className="rounded-[var(--radius-control)] bg-[color:var(--bg)] p-3">
                  <p className="text-2xl font-semibold tabular-nums">{rep.totals[k]}<span className="text-sm font-normal text-[color:var(--ink-2)]"> / {rep.totals.los}</span></p>
                  <p className="text-xs text-[color:var(--ink-2)]">{t(`align.stage.${k}`)}</p>
                </li>
              ))}
              <li className="rounded-[var(--radius-control)] bg-[color:var(--bg)] p-3">
                <p className="text-2xl font-semibold tabular-nums">{rep.totals.lessons}</p>
                <p className="text-xs text-[color:var(--ink-2)]">{t("align.lessons")}</p>
              </li>
            </ul>
          </section>

          <section className={`${card} overflow-x-auto`} aria-labelledby="align-weeks">
            <h2 id="align-weeks" className="mb-2 font-semibold">{t("align.timeline")}</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[color:var(--ink-2)]">
                  <th scope="col" className="py-1 pr-3">{t("align.week")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.lessons")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.stage.taught")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.stage.assessed")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.secureCum")}</th>
                </tr>
              </thead>
              <tbody>
                {rep.weeks.map((w) => (
                  <tr key={w.week_start} className="border-t">
                    <td className="py-1 pr-3 tabular-nums">{fmt(w.week_start)}</td>
                    <td className="py-1 pr-3 tabular-nums">{w.planned_lessons}</td>
                    <td className="py-1 pr-3 tabular-nums">{w.taught}</td>
                    <td className="py-1 pr-3 tabular-nums">{w.assessed}</td>
                    <td className="py-1 pr-3">
                      <span className="inline-block h-3 align-middle" style={{ width: `${(w.secure_cum / Math.max(rep.totals.los, 1)) * 160}px`, background: "var(--secure)" }} aria-hidden />
                      <span className="ml-2 tabular-nums">{w.secure_cum}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className={`${card} overflow-x-auto`} aria-labelledby="align-los">
            <h2 id="align-los" className="mb-2 font-semibold">{t("align.objectives")}</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[color:var(--ink-2)]">
                  <th scope="col" className="py-1 pr-3">{t("align.code")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.objective")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.stage.planned")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.stage.taught")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.stage.assessed")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.stage.secure")}</th>
                  <th scope="col" className="py-1 pr-3">{t("align.status")}</th>
                </tr>
              </thead>
              <tbody>
                {rep.los.map((lo) => (
                  <tr key={lo.lo_id} className="border-t align-top">
                    <td className="py-1 pr-3 font-mono">{lo.code}</td>
                    <td className="py-1 pr-3">{lo.text}</td>
                    <td className="py-1 pr-3 tabular-nums" title={lo.planned_dates.join(", ")}>{lo.planned}</td>
                    <td className="py-1 pr-3">{fmt(lo.taught_at)}</td>
                    <td className="py-1 pr-3">{fmt(lo.assessed_at)}</td>
                    <td className="py-1 pr-3 tabular-nums">{pct(lo.secure_share)} <span className="text-xs text-[color:var(--ink-2)]">({lo.secure}/{lo.people})</span></td>
                    <td className="py-1 pr-3"><span aria-hidden>{STATUS_GLYPH[lo.status]}</span> {t(`align.statuses.${lo.status}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className={card} aria-labelledby="align-narrative">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 id="align-narrative" className="font-semibold">{t("align.narrative")}</h2>
              <div className="print:hidden flex flex-wrap items-center gap-2">
                {base && <span className="text-xs text-[color:var(--ink-2)]">{t("align.version", { n: base.version, source: t(`align.sources.${base.source}`) })}</span>}
                <Button variant="secondary" loading={generate.isPending || !!gen.job} onClick={() => generate.mutate()}>{base ? t("align.redraft") : t("align.draft")}</Button>
              </div>
            </div>
            {gen.job && <p className="text-sm text-[color:var(--ink-2)]" aria-live="polite">{t("align.drafting")}</p>}
            {gen.error && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{gen.error}</p>}
            {!editing && !gen.job && <p className="text-sm text-[color:var(--ink-2)]">{t("align.noNarrative")}</p>}
            {editing && (
              <DraftFrame kind={base?.source === "ai" ? "draft" : "edited"}>
                <div className="flex flex-col gap-3">
                  <label className="flex flex-col gap-1 text-sm">{t("align.headline")}<input className="field" value={editing.headline} onChange={(e) => setEditing({ ...editing, headline: e.target.value })} /></label>
                  <label className="flex flex-col gap-1 text-sm">{t("align.summary")}<textarea className="field" rows={6} value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} /></label>
                  <label className="flex flex-col gap-1 text-sm">{t("align.highlights")}<textarea className="field" rows={3} value={editing.highlights.join("\n")} onChange={(e) => setEditing({ ...editing, highlights: e.target.value.split("\n").filter((x) => x.trim()) })} /></label>
                  {editing.gaps.length > 0 && (
                    <ul className="flex flex-col gap-1 text-sm" aria-label={t("align.gaps")}>
                      {editing.gaps.map((g, i) => (
                        <li key={`${g.lo_id}-${i}`} className="flex flex-wrap items-center gap-2">
                          <span className="font-mono">{codeOf(g.lo_id)}</span>
                          <input className="field field-sm min-w-0 flex-1" value={g.why} onChange={(e) => setEditing({ ...editing, gaps: editing.gaps.map((x, j) => (j === i ? { ...x, why: e.target.value } : x)) })} aria-label={t("align.gapWhy", { code: codeOf(g.lo_id) })} />
                          <Button variant="tertiary" onClick={() => setEditing({ ...editing, gaps: editing.gaps.filter((_, j) => j !== i) })}>{t("align.remove")}</Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <label className="flex flex-col gap-1 text-sm">{t("align.nextSteps")}<textarea className="field" rows={3} value={editing.next_steps.join("\n")} onChange={(e) => setEditing({ ...editing, next_steps: e.target.value.split("\n").filter((x) => x.trim()) })} /></label>
                  <div className="print:hidden flex items-center gap-2">
                    <Button disabled={!dirty || !editing.headline.trim()} loading={save.isPending} onClick={() => save.mutate(editing)}>{t("align.save")}</Button>
                    {base?.source === "ai" && <DraftChip />}
                    {save.error && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{String(save.error)}</p>}
                  </div>
                </div>
              </DraftFrame>
            )}
          </section>
        </>
      )}
    </TeacherChrome>
  );
}
